import type { ReactNode } from "react";
import { memo, useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Badge,
  Box,
  Collapse,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import type Commande from "models/Commande";
import { arrayUtil } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import Button from "@mui/material/Button";
import TuneIcon from "@mui/icons-material/Tune";
import Statut from "constants/Statut";
import StatutInput from "components/atoms/inputs/StatutInput";
import ArticleInput from "components/atoms/inputs/ArticleInput";
import type Article from "models/Article";
import MagasinDeRetraitInput from "components/atoms/inputs/MagasinDeRetraitInput";
import NumeroBonDeCommandeInput from "components/atoms/inputs/NumeroBonDeCommandeInput";
import commandeArticleUtil from "utils/commandeArticleUtil";

type CommandesFilterProps = {
  commandes: Array<Commande>;
  setFilteredCommandes: (c: Array<Commande>) => void;

  agentFilter?: boolean;
  articleFilter?: boolean;
  statutFilter?: boolean;
  centreRetraitFilter?: boolean;
  numeroBonDeCommandeFilter?: boolean;

  availableAgents?: Array<User>;
  availableArticles?: Array<Article>;
  availableStatuts?: Array<Statut>;

  selectedAgentIdDefault?: string;
  selectedStatutsDefault?: Array<Statut>;
  selectedArticlesDefault?: Array<Article>;
  selectedCentresRetraitDefault?: Array<string>;
};

const CommandesFilter = memo(function CommandesFilter({
  commandes = [],
  setFilteredCommandes,

  agentFilter = false,
  articleFilter = false,
  statutFilter = false,
  centreRetraitFilter = false,
  numeroBonDeCommandeFilter = false,

  availableAgents = [],
  availableArticles = [],
  availableStatuts = Object.values(Statut),

  selectedAgentIdDefault,
  selectedStatutsDefault = [],
  selectedArticlesDefault = [],
  selectedCentresRetraitDefault = [],
}: Readonly<CommandesFilterProps>): ReactNode {
  const [open, setOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<User | undefined>(
    availableAgents.find((a: User) => a.id === selectedAgentIdDefault)
  );
  const [selectedStatuts, setSelectedStatuts] = useState<Array<Statut>>(selectedStatutsDefault);
  const [selectedArticles, setSelectedArticles] = useState<Array<Article>>(selectedArticlesDefault);
  const [selectedCentresRetrait, setSelectedCentresRetrait] = useState<Array<string>>(
    selectedCentresRetraitDefault
  );
  const [uniqueNBonDeCommande, setUniqueNBonDeCommande] = useState<Array<string>>([]);
  const [numeroBonDeCommandeSearchString, setNumeroBonDeCommandeSearchString] = useState<string>();
  const [nbrFilter, setNbrFilter] = useState<number>();
  const [filteredCommandesCount, setFilteredCommandesCount] = useState<number>(0);

  const filterCommandes = useCallback(() => {
    let filteredCommandes = [...commandes];
    if (selectedAgent != null) {
      filteredCommandes = filteredCommandes.filter(
        (commande: Commande) => commande.demandeurId === selectedAgent?.id
      );
    }
    if (selectedArticles.length > 0) {
      filteredCommandes = filteredCommandes.filter((commande: Commande) =>
        arrayUtil.isCommonElements(
          selectedArticles.map((a) => a.id),
          commande.articles.map((a) => a.articleId)
        )
      );
    }
    if (selectedStatuts.length > 0) {
      filteredCommandes = filteredCommandes.filter((commande: Commande) =>
        selectedStatuts.map((c) => c.toLowerCase()).includes(commande.statutCommande.toLowerCase())
      );
    }
    if (selectedCentresRetrait.length > 0) {
      filteredCommandes = filteredCommandes.filter((commande: Commande) =>
        selectedCentresRetrait
          .map((c) => c.toLowerCase())
          .includes(commande.magasinDeRetrait.toLowerCase())
      );
    }
    if (numeroBonDeCommandeSearchString != null && numeroBonDeCommandeSearchString.trim() !== "") {
      const numeroRegex = new RegExp(`^${numeroBonDeCommandeSearchString.trim()}$`, "i");
      filteredCommandes = filteredCommandes.filter((commande: Commande) =>
        commande.articles.some((article) => numeroRegex.test(article.numeroBonDeCommande ?? ""))
      );
    }
    return filteredCommandes;
  }, [
    selectedStatuts,
    selectedAgent,
    selectedCentresRetrait,
    selectedArticles,
    numeroBonDeCommandeSearchString,
    commandes,
  ]);

  useEffect(() => {
    const commandes = filterCommandes();
    setFilteredCommandesCount(commandes.length);
    setFilteredCommandes(commandes);

    const nbStatut = selectedStatuts.length;
    const nbAgent = selectedAgent == null ? 0 : 1;
    const nbCentres = selectedCentresRetrait.length;
    const nbArticles = selectedArticles.length;
    const nbBonCommande = (numeroBonDeCommandeSearchString ?? "").trim() === "" ? 0 : 1;
    setNbrFilter(nbStatut + nbAgent + nbCentres + nbArticles + nbBonCommande);
    // TODO: revoir les dépendances lors d'une mise à jour des règles ESLint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedStatuts,
    selectedAgent,
    selectedCentresRetrait,
    selectedArticles,
    numeroBonDeCommandeSearchString,
    commandes,
  ]);
  useEffect(() => {
    setUniqueNBonDeCommande(
      commandeArticleUtil.getUniquesNumerosBonDeCommandeFromCommandes(commandes)
    );
  }, [commandes]);

  return (
    <Grid container spacing={2} size={12}>
      <Grid size={12}>
        <Badge color="error" badgeContent={nbrFilter}>
          <Button
            variant="contained"
            startIcon={<TuneIcon />}
            onClick={() => {
              setOpen(!open);
            }}>
            Filtres
          </Button>
        </Badge>
      </Grid>
      <Grid container spacing={2} sx={{ margin: 0 }} size={12}>
        <Collapse in={open} unmountOnExit sx={{ width: "100%" }}>
          <Grid container spacing={2} size={12}>
            {agentFilter && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <Autocomplete
                    disablePortal
                    size="small"
                    renderInput={(params) => (
                      <TextField {...params} label="Nom d'un agent" placeholder="Nom d'un agent" />
                    )}
                    value={selectedAgent ?? null}
                    onChange={(_e, newValue) => {
                      setSelectedAgent(newValue ?? undefined);
                    }}
                    options={availableAgents}
                    renderOption={(props, option: User) => (
                      <Box component="li" {...props} key={option.id}>
                        {option.displayName}
                      </Box>
                    )}
                    getOptionLabel={(option: User) => option.displayName}
                    limitTags={3}
                  />
                </FormControl>
              </Grid>
            )}
            {articleFilter && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <ArticleInput
                  multiple
                  availableArticles={availableArticles}
                  selectedArticles={selectedArticles}
                  setSelectedArticles={setSelectedArticles}
                />
              </Grid>
            )}
            {statutFilter && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <StatutInput
                  selectedStatuts={selectedStatuts}
                  setSelectedStatuts={setSelectedStatuts}
                  availableStatuts={availableStatuts}
                  label="État(s) de la commande"
                />
              </Grid>
            )}
            {centreRetraitFilter && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <MagasinDeRetraitInput
                  multiple
                  label="Magasin de retrait"
                  selectedCentresRetrait={selectedCentresRetrait}
                  setSelectedCentresRetrait={setSelectedCentresRetrait}
                />
              </Grid>
            )}
            {numeroBonDeCommandeFilter && (
              <Grid size={{ xs: 12, md: 4 }}>
                <NumeroBonDeCommandeInput
                  numeroBonDeCommande={numeroBonDeCommandeSearchString}
                  setNumeroBonDeCommande={setNumeroBonDeCommandeSearchString}
                  availableNumerosBonDeCommande={uniqueNBonDeCommande}
                />
              </Grid>
            )}
          </Grid>
        </Collapse>
      </Grid>
      <Grid size={12}>
        {filteredCommandesCount <= 0 ? (
          <Typography>Aucune commande à afficher</Typography>
        ) : (
          <Typography>{`${filteredCommandesCount} commande(s)`}</Typography>
        )}
      </Grid>
    </Grid>
  );
});

export default CommandesFilter;
